/**
 * Create a list. A segment needs an audience and starts with an empty
 * "match all" group; a static list is `mixed` and holds hand-picked members.
 * The kind cannot be changed afterwards (`kind` is create-only on the wire), so
 * the choice is made here and nowhere else.
 */
import { useRef, useState, type KeyboardEvent } from 'react';
import { Button, Field, Input, Modal, Textarea } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { api, ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import type { MarketingList } from '@/lib/types';

type Kind = 'segment' | 'static';
type Audience = 'contacts' | 'subscribers';

/**
 * ⚠️ THE ONE SANCTIONED EXCEPTION TO THE NO-HELPER-TEXT RULE (owner, 2026-10-03):
 * the Kind and Audience choices carry ONE short descriptive line each, because
 * the kind is create-only and cannot be changed afterwards. Nothing else in
 * this modal gains explanatory text.
 *
 * The audience lines were checked against the backend's segment compiler:
 * `contacts` resolves `d_contacts` rows (company rules reach a contact through
 * its company, and a contact with no company is still a member), and
 * `subscribers` resolves `d_marketing_subscribers`, which are added or imported
 * from the lists screens — they are not EMERGE contacts.
 */
const KIND_OPTIONS: readonly ChoiceOption<Kind>[] = [
  { value: 'segment', label: 'Live segment', description: 'Updates itself as contacts change, from rules you set.' },
  { value: 'static', label: 'Static list', description: 'Only the people you add or import.' },
];

const AUDIENCE_OPTIONS: readonly ChoiceOption<Audience>[] = [
  { value: 'contacts', label: 'Contacts', description: 'Contacts recorded in EMERGE, with their companies.' },
  { value: 'subscribers', label: 'Subscribers', description: 'People added or imported as marketing subscribers.' },
];

interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  description: string;
}

/**
 * Radio cards, module-local on purpose — the owner ruled these are NOT a new
 * `ui` primitive. A real `radiogroup`: one tab stop (the checked card), arrow
 * keys move and select, Space selects. Focus uses the house `:focus-visible`
 * outline from `index.css` (it matches `[tabindex]`), so nothing local overrides
 * it. The selected card is marked by a filled
 * radio dot and a heavier border/weight, never by colour alone.
 */
function ChoiceCards<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
  disabled = false,
  error,
}: {
  id: string;
  label: string;
  value: T;
  options: readonly ChoiceOption<T>[];
  onChange(value: T): void;
  disabled?: boolean;
  error?: string;
}) {
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const labelId = `${id}-label`;
  const errorId = `${id}-error`;

  const select = (index: number) => {
    const option = options[index];
    if (!option) return;
    refs.current[index]?.focus();
    if (option.value !== value) onChange(option.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>, index: number) => {
    if (disabled) return;
    const last = options.length - 1;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        select(index === last ? 0 : index + 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        select(index === 0 ? last : index - 1);
        break;
      case 'Home':
        event.preventDefault();
        select(0);
        break;
      case 'End':
        event.preventDefault();
        select(last);
        break;
      case ' ':
        event.preventDefault();
        select(index);
        break;
      default:
        break;
    }
  };

  return (
    <div className="grid gap-1.5">
      <span id={labelId} className="text-sm font-medium text-fg-secondary">
        {label}
      </span>
      <div
        role="radiogroup"
        aria-labelledby={labelId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        aria-disabled={disabled || undefined}
        className="grid gap-2 sm:grid-cols-2"
      >
        {options.map((option, index) => {
          const checked = option.value === value;
          const titleId = `${id}-${option.value}-title`;
          const descId = `${id}-${option.value}-desc`;
          return (
            <div
              key={option.value}
              ref={(node) => {
                refs.current[index] = node;
              }}
              role="radio"
              aria-checked={checked}
              aria-labelledby={titleId}
              aria-describedby={descId}
              aria-disabled={disabled || undefined}
              tabIndex={disabled ? -1 : checked ? 0 : -1}
              onClick={() => {
                if (!disabled) select(index);
              }}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cx(
                'flex min-w-0 cursor-pointer items-start gap-2.5 rounded-md border bg-surface p-3 text-left',
                'transition-colors duration-150 ease-out motion-reduce:transition-none',
                checked
                  ? 'border-2 border-fg bg-surface-raised p-[11px]'
                  : 'border-border hover:bg-surface-hover',
                error && !checked && 'border-danger-border',
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <span
                aria-hidden="true"
                className={cx(
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2',
                  checked ? 'border-fg' : 'border-hairline-strong',
                )}
              >
                {checked && <span className="h-2 w-2 rounded-full bg-fg" />}
              </span>
              <span className="grid min-w-0 gap-0.5">
                <span id={titleId} className={cx('text-sm text-fg', checked ? 'font-semibold' : 'font-medium')}>
                  {option.label}
                </span>
                <span id={descId} className="text-xs text-fg-muted">
                  {option.description}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      {error && (
        <p id={errorId} className="m-0 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export function NewListModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose(): void;
  onCreated(list: MarketingList): void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<Kind>('segment');
  const [audience, setAudience] = useState<Audience>('contacts');
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setDescription('');
    setKind('segment');
    setAudience('contacts');
    setFieldErrors({});
    setFormError(null);
  };

  const close = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (saving) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setFieldErrors({ name: 'A list needs a name.' });
      return;
    }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const body =
        kind === 'segment'
          ? {
              name: trimmed,
              ...(description.trim() ? { description: description.trim() } : {}),
              kind,
              audience,
              definition: { match: 'all' as const, rules: [] },
            }
          : {
              name: trimmed,
              ...(description.trim() ? { description: description.trim() } : {}),
              kind,
            };
      const result = await api.createMarketingList(body);
      reset();
      onCreated(result.data);
    } catch (error) {
      if (error instanceof ApiError) {
        const { fields, unroutable } = error.routeFieldErrors(['name', 'description', 'kind', 'audience']);
        setFieldErrors(fields);
        const messages = unroutable;
        setFormError(
          messages.length > 0
            ? messages.join(' ')
            : Object.keys(fields).length > 0
              ? null
              : (error.formMessage ?? error.message),
        );
      } else {
        setFormError('The list could not be created.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="New list"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="new-marketing-list" loading={saving}>
            Create list
          </Button>
        </div>
      }
    >
      <form id="new-marketing-list" onSubmit={submit} className="grid gap-4">
        {formError && (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        )}
        <Field label="Name" required error={fieldErrors.name}>
          {(props) => (
            <Input
              {...props}
              value={name}
              maxLength={160}
              onChange={(e) => setName(e.target.value)}
              invalid={Boolean(fieldErrors.name)}
            />
          )}
        </Field>
        <Field label="Description" error={fieldErrors.description}>
          {(props) => (
            <Textarea
              {...props}
              rows={2}
              value={description}
              maxLength={500}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <ChoiceCards<Kind>
          id="new-list-kind"
          label="Kind"
          value={kind}
          options={KIND_OPTIONS}
          onChange={setKind}
          disabled={saving}
          error={fieldErrors.kind}
        />
        {kind === 'segment' && (
          <ChoiceCards<Audience>
            id="new-list-audience"
            label="Audience"
            value={audience}
            options={AUDIENCE_OPTIONS}
            onChange={setAudience}
            disabled={saving}
            error={fieldErrors.audience}
          />
        )}
      </form>
    </Modal>
  );
}
